import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Search, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { publications, research, conferences } from '../data/mock';
import GlassCard from '../components/GlassCard';
import NeonText from '../components/NeonText';

const Research = () => {
  const [filter, setFilter] = useState('all');
  const [expandedPublications, setExpandedPublications] = useState({});

  const filteredPublications = filter === 'all' 
    ? publications 
    : publications.filter(pub => pub.category.toLowerCase() === filter.toLowerCase());

  const togglePublication = (index) => {
    setExpandedPublications(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section with Glass Morphism */}
      <section className="pt-32 pb-20 bg-[#121212] relative overflow-hidden">
        {/* Animated background effect */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#00FFD1] rounded-full filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#00FFD1] rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-6xl font-bold text-white mb-6">
              <NeonText>Research & Publications</NeonText>
            </h1>
            <p className="text-2xl text-white text-opacity-70 max-w-3xl mx-auto">
              Contributing to the advancement of Ayurvedic science through research and innovation
            </p>
          </motion.div>
        </div>
      </section>

      {/* Ongoing Research with Glass Cards */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-5xl font-bold text-white mb-16 text-center"
          >
            <NeonText>Ongoing Research Projects</NeonText>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {research.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard neonBorder neonColor="#00FFD1">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#00FFD1] bg-opacity-20 flex items-center justify-center flex-shrink-0">
                      <Search className="text-[#00FFD1]" size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-3">{project.title}</h3>
                      <div className="inline-block px-4 py-1 bg-[#00FFD1] bg-opacity-20 border border-[#00FFD1]">
                        <span className="text-[#00FFD1] text-sm font-medium">{project.status}</span>
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Publications with Expandable Details */}
      <section className="py-20 bg-[#121212] relative overflow-hidden">
        {/* Background glow effect */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#00FFD1] rounded-full filter blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-5xl font-bold text-white mb-8 text-center"
          >
            <NeonText>My Published Works</NeonText>
          </motion.h2>

          {/* Filter Buttons with Glass Effect */}
          <div className="flex flex-wrap gap-4 justify-center mb-16">
            {['all', 'research', 'clinical', 'international', 'theory'].map((category) => (
              <motion.button
                key={category}
                onClick={() => setFilter(category)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-6 py-3 font-medium text-lg transition-all duration-400 backdrop-blur-xl ${
                  filter === category
                    ? 'bg-[#00FFD1] text-black shadow-lg'
                    : 'bg-white bg-opacity-10 text-white border border-white border-opacity-20 hover:bg-white hover:bg-opacity-20'
                }`}
                style={{
                  boxShadow: filter === category ? '0 0 30px rgba(0, 255, 209, 0.5)' : 'none'
                }}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </motion.button>
            ))}
          </div>

          {/* Expandable Publications List */}
          <div className="space-y-4">
            {filteredPublications.map((pub, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <GlassCard className="cursor-pointer" neonBorder={expandedPublications[index]}>
                  <div 
                    onClick={() => togglePublication(index)}
                    className="flex items-start gap-6"
                  >
                    <div className="w-12 h-12 bg-[#00FFD1] bg-opacity-20 flex items-center justify-center flex-shrink-0">
                      <FileText className="text-[#00FFD1]" size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-2xl font-bold text-white pr-4">{pub.title}</h3>
                        <div className="flex items-center gap-4">
                          <span className="text-[#00FFD1] font-medium text-lg">{pub.year}</span>
                          {expandedPublications[index] ? (
                            <ChevronUp className="text-[#00FFD1]" size={24} />
                          ) : (
                            <ChevronDown className="text-[#00FFD1]" size={24} />
                          )}
                        </div>
                      </div>
                      
                      {/* Collapsed view */}
                      {!expandedPublications[index] && (
                        <div className="flex items-center gap-4">
                          <div className="inline-block px-4 py-1 bg-white bg-opacity-10">
                            <span className="text-white text-opacity-70 text-sm">{pub.category}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Expanded view */}
                      <AnimatePresence>
                        {expandedPublications[index] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="pt-4 border-t border-white border-opacity-20 space-y-3">
                              <div>
                                <p className="text-white text-opacity-60 text-sm mb-1">Journal/Conference:</p>
                                <p className="text-white text-opacity-85 text-lg">{pub.journal}</p>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="inline-block px-4 py-2 bg-[#00FFD1] bg-opacity-20 border border-[#00FFD1]">
                                  <span className="text-[#00FFD1] text-sm font-medium">{pub.category}</span>
                                </div>
                                <div className="inline-block px-4 py-2 bg-white bg-opacity-10">
                                  <span className="text-white text-opacity-70 text-sm">Published: {pub.year}</span>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Conferences with Glass Cards */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-5xl font-bold text-white mb-16 text-center"
          >
            <NeonText>Conferences & Workshops</NeonText>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {conferences.map((conf, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard neonBorder neonColor="#00FFD1">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#00FFD1] bg-opacity-20 flex items-center justify-center">
                        <BookOpen className="text-[#00FFD1]" size={20} />
                      </div>
                      <span className="text-[#00FFD1] font-medium text-lg">{conf.year}</span>
                    </div>
                    <div className="px-4 py-1 bg-[#00FFD1] bg-opacity-20 border border-[#00FFD1]">
                      <span className="text-[#00FFD1] text-sm font-medium">{conf.type}</span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{conf.title}</h3>
                  <p className="text-white text-opacity-70 mb-3">{conf.role}</p>
                  {conf.location && (
                    <p className="text-white text-opacity-60 text-sm mb-4">{conf.location}</p>
                  )}
                  {conf.topics && (
                    <div className="flex flex-wrap gap-2">
                      {conf.topics.map((topic, i) => (
                        <div key={i} className="px-3 py-1 bg-white bg-opacity-10 backdrop-blur-sm">
                          <span className="text-white text-opacity-70 text-sm">{topic}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Research;